



















































































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { TranslationsTypes } from "@/data/enums/translations";

// Typings
import { ITicketsForm, ITicketDepartment } from "@/models/tickets";
import { IState } from "@/store";
import { IBrand } from "@/models/brands";

@Component({
  computed: {
    ...mapState({
      brand(state: IState) {
        return state.brand;
      }
    })
  }
})
export default class AddEditTicketDepartmentModal extends Mixins(
  HasValidation
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    name: HTMLFormElement;
  };

  // Getters
  public brand!: IBrand;

  @Prop({ default: () => ({}) })
  public department!: ITicketDepartment;

  public form: ITicketsForm = {
    name: "",
    code: "",
    username: null,
    default: false,
    is_public: true
  };
  public formClone: ITicketsForm = this.$_.clone(this.form);

  public isProcessing = false;

  public created() {
    this.setInitialForm();
  }

  public setInitialForm() {
    if (!this.isCreate) {
      this.form = this.$_.merge(
        {},
        this.form,
        this.$_.pick(this.department, [
          "name",
          "code",
          "username",
          "is_public",
          "default"
        ])
      );
    }
    this.formClone = this.$_.clone(this.form);
  }

  public nullify(key, val, vm = this) {
    const value = val && val.length ? val : null;
    this.$set(vm, key, value);
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.formIsChanged ||
      this.hasErrors ||
      !this.canManageDepartment
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canManageDepartment) {
      return;
    }
    this.isProcessing = true;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      await this.saveTicketDepartment();

      this.$store.dispatch("toast/show", {
        message: this.$t(
          `_.object_successfully_${this.isCreate ? "added" : "updated"}`,
          { object: this.$tc("_.ticket_departments", 1) }
        )
      });
      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async saveTicketDepartment() {
    return this.$store.dispatch(
      `data/${DataModules.TICKETS_DEPARTMENTS}/${
        this.isCreate ? "create" : "update"
      }`,
      this.$_.merge(
        {
          data: this.getNewValues
        },
        this.isCreate ? {} : { id: this.department.id }
      )
    );
  }

  public openTranslateModal(props) {
    if (!this.canTranslateProduct) return;
    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          brandId: this.department.brand_id,
          objectId: this.department.id,
          objectTypeCode: TranslationsTypes.TICKET_DEPARTMENT,
          fieldType: "text",
          ...props
        }
      }
    );
  }

  public formatValue(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/\s/g, "-")
      .replace(/[-]{2,}/g, "-");
  }

  public get getNewValues() {
    if (this.isCreate) return this.form;
    return this.$_.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });
  }

  @Watch("form.name")
  public onNameChange(newValue, oldValue) {
    if (this.form.code === this.formatValue(oldValue)) {
      this.form.code = this.formatValue(newValue);
    }
  }

  @Watch("form.default")
  public onDefaultChange() {
    if (!this.form.default) {
      return;
    }
    this.form.is_public = true;
  }

  public get mailboxHostname() {
    return `${this.$_.get(this.brand, "code")}.${
      process.env.VUE_APP_UPMIND_APP_DOMAIN
    }`;
  }

  public get isCreate() {
    return this.$_.isEmpty(this.department);
  }

  public get canManageDepartment() {
    return this.isCreate
      ? this.$userCan("create_tickets_departments")
      : this.$userCan("update_tickets_departments");
  }

  public get canTranslateProduct() {
    return (
      !this.isCreate &&
      this.$userCan("create_object_translation") &&
      this.$userCan("get_object_translation") &&
      this.$userCan("list_object_translations")
    );
  }

  public get getGuardFunctionalities() {
    return ["get_tickets_departments"];
  }
}
